import React from 'react';
import {string, func, instanceOf} from 'prop-types';
import Cropper from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import {Button, Message} from 'semantic-ui-react';
import ImageDimensions, {GIFType} from 'utils/ImageDimensions';
import {isNoU} from 'utils/DBUtils';

const GIF_CONTENT_TYPE = 'image/gif';

/*
    The crop step shown after picking a file in "My Device", or opting into
    cropping a "Link (URL)" source. Either way, the parent modal
    (ImageUploadModal) always hands this component a same-origin `File` --
    for a device upload that's the File the browser picker/drop handler
    produced; for a Link source it's the Blob fetched through admin-api's
    GET /image/fetch (§6's RemoteImageFetcher), wrapped in a File by the
    modal before it ever reaches here. See the approach doc's "The crop
    canvas only ever sees same-origin bytes" paragraph for why: this keeps
    the crop canvas untainted unconditionally, with no special-attribute
    image load, no CORS probe, and no host-dependent failure mode to
    handle here at all -- that guardrail now lives server-side, once, in
    the fetcher.

    Mirrors the aspect-ratio calculation in observables/lettables/cropOriginal.js:8-52:
      - aspect ratio comes from ImageDimensions[imageType].retina (x / y)
      - the crop is free-form when either retina axis is null
      - GIFs are never cropped (re-encoding an animated GIF flattens it to
        its first frame), matching cropOriginal.js:45-48 and the GIFType
        override in pages/Images/epics.js:320-322

    Exposes getCroppedFile() (a Promise) so the parent modal's Confirm
    button can pull the result out imperatively, the way the surrounding
    class-based code already does with things like getCroppedCanvas().
*/
export default class ImageCropStep extends React.Component {
  constructor(props) {
    super(props);
    this.cropperRef = React.createRef();
    // `file`/`imageType` don't change over this component's lifetime -- the
    // parent unmounts this step (via onReplace) before a different source
    // can be picked -- so it's safe to resolve these once.
    this.previewSrc = URL.createObjectURL(props.file);
    this.isGif = props.file.type === GIF_CONTENT_TYPE;
    this.effectiveImageType = this.isGif ? GIFType : props.imageType;
    this.dims = ImageDimensions[this.effectiveImageType];
  }

  componentDidMount() {
    if (isNoU(this.dims)) {
      // Surfaced as a UI error rather than thrown, so picking a
      // misconfigured field doesn't crash the whole modal -- see
      // cropOriginal.js:13-15 for the equivalent (thrown) check.
      this.props.onError(`Image type ${this.props.imageType} is not in image dimensions map.`);
    }
  }

  componentWillUnmount() {
    URL.revokeObjectURL(this.previewSrc);
  }

  getAspectRatio = () => {
    if (isNoU(this.dims)) {
      return undefined;
    }
    const retina = this.dims.retina;
    if (isNoU(retina.x) || isNoU(retina.y)) {
      // Free-form crop when either axis is unconstrained.
      return undefined;
    }
    return retina.x / retina.y;
  };

  // Returns a Promise<{file, name, contentType}>
  getCroppedFile = () => {
    if (this.isGif) {
      // Not cropped -- re-encoding would flatten the animation.
      return Promise.resolve({file: this.props.file, name: this.props.file.name, contentType: this.props.file.type});
    }

    const name = this.props.file.name;
    const contentType = this.props.file.type;

    return new Promise((resolve, reject) => {
      if (isNoU(this.cropperRef.current)) {
        reject(new Error('The crop editor is not ready yet.'));
        return;
      }

      const canvas = this.cropperRef.current.getCroppedCanvas();
      if (isNoU(canvas)) {
        reject(new Error('Unable to crop this image.'));
        return;
      }

      canvas.toBlob((blob) => {
        if (isNoU(blob)) {
          reject(new Error('Unable to crop this image.'));
          return;
        }
        resolve({
          file: new File([blob], name, {type: contentType}),
          name,
          contentType
        });
      }, contentType);
    });
  };

  render() {
    const replaceLabel = this.props.replaceLabel || 'Choose a different file';

    if (isNoU(this.dims)) {
      return (
        <div className="db-image-crop-step">
          <Message negative>{`Image type ${this.props.imageType} is not in image dimensions map.`}</Message>
          <Button type="button" basic onClick={this.props.onReplace}>{replaceLabel}</Button>
        </div>
      );
    }

    return (
      <div className="db-image-crop-step">
        {this.isGif ? (
          <div className="db-image-crop-preview">
            <img src={this.previewSrc} alt={this.props.file.name} style={{maxWidth: '100%'}} />
            <p>Animated GIFs are uploaded as-is and are not cropped.</p>
          </div>
        ) : (
          <Cropper
            ref={this.cropperRef}
            src={this.previewSrc}
            style={{height: 400, width: '100%'}}
            aspectRatio={this.getAspectRatio()}
            viewMode={1}
            guides={true}
            responsive={true}
            autoCropArea={1} // start the crop box at the full image, not cropperjs's default ~80%
          />
        )}
        <div className="db-image-crop-step-actions">
          <Button type="button" basic onClick={this.props.onReplace}>{replaceLabel}</Button>
        </div>
      </div>
    );
  }
}

ImageCropStep.propTypes = {
  file: instanceOf(File).isRequired,
  imageType: string.isRequired,
  replaceLabel: string,
  onError: func.isRequired,
  onReplace: func.isRequired
};



// WEBPACK FOOTER //
// ./public/components/images/ImageCropStep.jsx